/**
 * Master data — `docs/API.md` §10.
 * Mirrors `salescrmapi/src/modules/masterdata`.
 *
 * Twenty-one reference tables behind one uniform endpoint, so every function
 * here takes the entity key as its first argument. The keys are snake_case
 * (`lead_statuses`, `opportunity_stages`); the API accepts hyphens too, but this
 * module uses the canonical form so cache keys cannot end up split across two
 * spellings of the same list.
 *
 * There is deliberately **no mock fallback** in this module. `withMockFallback`
 * exists for endpoints that answer `501`, and §10 is built — a fixture here
 * would be a second, drifting definition of a shape the server already returns,
 * and the screen's whole design depends on the field catalogue being the real
 * one.
 */
import apiClient, { ifMatch, idempotencyKey } from './axiosClient.js';

/**
 * The catalogue: every entity with its field schema, grouped for the picker.
 *
 * This is what makes the admin screen schema-driven rather than twenty-one
 * hand-written forms (requirement §54). Each entity carries `fields[]` with the
 * type, label, grid width, options and nullability of every column, plus
 * `ordered` (whether `/reorder` works at all), `scoped_by` (a parent the list
 * filters on) and a live `record_count`.
 *
 * @returns {Promise<{groups:{name:string, entities:object[]}[]}>}
 */
export async function listEntities() {
  const { data } = await apiClient.get('/master-data/entities');
  return data;
}

/**
 * A page of records for one entity.
 *
 * `with_usage` defaults to true server-side and costs one grouped count per
 * referencing foreign key — seven for countries. The screen keeps it on because
 * the count is what tells an administrator whether a row can be deleted at all;
 * a picker that only needs names should turn it off.
 *
 * @param {string} entity e.g. 'lead_statuses'
 * @param {{limit?:number, cursor?:string, q?:string, is_active?:boolean,
 *          scope_id?:string, with_usage?:boolean, include_deleted?:boolean}} params
 * @returns {Promise<{entity:string, data:object[], page:object}>}
 */
export async function listRecords(entity, params = {}) {
  const { data } = await apiClient.get(`/master-data/${entity}`, { params });
  return data;
}

/** One record, with a `references` breakdown of what points at it. */
export async function getRecord(entity, id) {
  const { data } = await apiClient.get(`/master-data/${entity}/${id}`);
  return data;
}

/**
 * Create a record.
 *
 * Carries an `Idempotency-Key`, so a double-submitted form cannot create the
 * row twice — which on a table with a unique code would otherwise surface as a
 * confusing `409` on the second attempt.
 */
export async function createRecord(entity, payload) {
  const { data } = await apiClient.post(`/master-data/${entity}`, payload, { headers: idempotencyKey() });
  return data;
}

/**
 * Update a record.
 *
 * `rowVersion` becomes `If-Match` (§1.7). A mismatch is `409 stale_write`
 * carrying `current_version`, `changed_fields` and the full current record, so
 * the caller can show what changed underneath them rather than just refusing.
 */
export async function updateRecord(entity, id, payload, rowVersion) {
  const { data } = await apiClient.patch(`/master-data/${entity}/${id}`, payload, { headers: ifMatch(rowVersion) });
  return data;
}

/**
 * Delete a record.
 *
 * `reason` is mandatory — `iam.app_operation.requires_reason` is true for
 * `delete`. Refused with `409 master_data_in_use` while anything still
 * references the row; the error payload carries `references` (counts per
 * entity) and a `suggestion`, which the UI shows instead of a bare failure.
 *
 * `row_version` is optional here, unlike PATCH: two administrators deleting the
 * same retired picklist entry both got what they wanted.
 */
export async function deleteRecord(entity, id, { reason, rowVersion } = {}) {
  await apiClient.delete(`/master-data/${entity}/${id}`, {
    data: { reason, ...(rowVersion != null ? { row_version: rowVersion } : {}) },
  });
}

/**
 * Set the display order.
 *
 * Position in `order` becomes `sort_order`, starting at 1. Only entities whose
 * catalogue entry says `ordered` accept this; the rest answer `422 not_ordered`
 * rather than silently doing nothing.
 *
 * An id that matches nothing fails the whole call, so a stale list cannot leave
 * the order half-applied.
 *
 * @param {string[]} order ids in their new order
 * @param {string} [scopeId] required for opportunity_stages — order is unique per pipeline
 */
export async function reorderRecords(entity, order, scopeId) {
  const { data } = await apiClient.post(`/master-data/${entity}/reorder`, {
    order,
    ...(scopeId ? { scope_id: scopeId } : {}),
  });
  return data;
}
